import { defineStore } from "pinia";
import { useAuthStore } from "./auth.store";
import FiltersService from "@/services/FiltersService";
import { i18n } from "@/main";

// Definiujemy store Pinia dla filtrów kalibracji
export const useCalibrationFiltersStore = defineStore({
  // Unikalne id store'a
  id: "userActivityFilter",
  
  // Stan początkowy store'a
  state: () => ({
    filters: {
      // Początek zakresu dat: 24 godziny temu (ustawione dynamicznie)
      dateTimeStart: new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).toISOString(),
      // Koniec zakresu dat: teraz
      dateTimeEnd: new Date().toISOString(),
      // Indeks wybranej opcji rozdzielczości (0 = pierwsza opcja z resolutionOptions)
      statResolutionUnit: 0,
      // Wartość rozdzielczości w godzinach (domyślna: 2 godziny)
      statResolutionValue: 2,
    },
    // Tablica możliwych wartości rozdzielczości (w godzinach)
    resolution: [2, 4, 6, 8, 12, 24, 48, 72, 168],
    
    // Lista użytkowników (pobrana z serwisu)
    users: [],
    // Lista lokalizacji (pobrana z serwisu)
    locations: [],
    
    // Mapa kluczy do opisów typów zdarzeń
    eventTypes: {
      1: "Logowanie do strony WWW",
      2: "Logowanie do stacji preselekcji",
      3: "Aktywność VMS",
    },
    
    // Odwołanie do store'a autoryzacji (przechowuje np. errorMessage)
    authStore: useAuthStore(),
    
    // Tablica kluczy obowiązkowych pól (do walidacji)
    requireFields: [],
    // Komunikat błędu walidacji
    error: null,
  }),

  // Gettery – wyliczone wartości na podstawie stanu
  getters: {
    /**
     * Zwraca etykietę wyświetlaną w select dla rozdzielczości.
     * Jeśli wartość >= 24h, przelicza na dni.
     */
    resolutionLabel(state) {
      const v = state.filters.statResolutionValue;
      if (v >= 24) {
        const days = v / 24;
        return days === 1 ? '1 dzień' : `${days} dni`;
      }
      return v === 1 ? '1 godzina' : `${v} godzin`;
    }
  },

  // Akcje – metody modyfikujące stan store'a
  actions: {
    /**
     * Ustawia wartość rozdzielczości, jeśli jest w resolutionOptions.
     * @param {number} val - liczba godzin
     */
    setResolution(val) {
      if (!this.resolution.includes(val)) {
        console.warn(`Nieobsługiwana rozdzielczość: ${val}h`);
        return;
      }
      this.filters.statResolutionValue = val;
    },

    /**
     * Pobiera listę lokalizacji z backendu tylko raz.
     */
    getLocations() {
      if (this.locations.length === 0) {
        FiltersService.getLocations()
          .then((data) => {
            // Zapisujemy otrzymane dane do store
            this.locations = data;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    },

    /**
     * Pobiera listę użytkowników z backendu tylko raz.
     */
    getUsers() {
      if (this.users.length === 0) {
        FiltersService.getUsers()
          .then((data) => {
            const tmpArray = [];
            if (Array.isArray(data) && data.length > 0) {
              // Mapowanie odpowiedzi na obiekt {id, name}
              for (const us of data) {
                tmpArray.push({ id: us.id, name: us.username });
              }
              this.users = tmpArray;
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    },

    /**
     * Dodaje element do odpowiedniego filtra bazując na nazwie.
     * Rozróżnia logikę dla różnych typów filtrów.
     */
    addItem(name, value) {
      switch (name) {
        case "Resolution": {
          // Przypisanie statResolutionValue na podstawie indeksu
          switch (value) {
            case 0: this.filters.statResolutionValue = 2; break;
            case 1: this.filters.statResolutionValue = 4; break;
            case 2: this.filters.statResolutionValue = 6; break;
            case 3: this.filters.statResolutionValue = 8; break;
            case 4: this.filters.statResolutionValue = 12; break;
            case 5: this.filters.statResolutionValue = 24; break;
            case 6: this.filters.statResolutionValue = 48; break;
            case 7: this.filters.statResolutionValue = 72; break;
            case 8: this.filters.statResolutionValue = 168; break;
            default: this.filters.statResolutionValue = 1; break;
          }
          break;
        }
        case "LocationList": {
          // Dodawanie lub usuwanie lokalizacji z tablicy
          if (this.filters.locations) {
            const idx = this.filters.locations.indexOf(value);
            if (idx === -1) {
              this.filters.locations.push(value);
            } else {
              this.filters.locations.splice(idx, 1);
            }
          } else {
            this.filters.locations = [value];
          }
          // Jeśli nie ma żadnej lokalizacji, usuwamy klucz
          if (this.filters.locations.length === 0) {
            delete this.filters.locations;
          }
          break;
        }
        // Kolejne case'y dla innych filtrów (VehicleCategories, LaneNumbers,...)
        
        default: {
          // Dla nieobsługiwanych nazw po prostu zapisujemy wartość
          this.filters[name] = value;
        }
      }
    },

    /**
     * Usuwa filtr o podanej nazwie.
     */
    removeItem(name) {
      switch (name) {
        case "rangeOfDate":
          delete this.filters.dateTimeStart;
          delete this.filters.dateTimeEnd;
          break;
        case "violations":
          // Usuwanie wszystkich kluczy z mapy violations
          Object.keys(this.violations).forEach((key) => delete this.filters[key]);
          break;
        default:
          // Domyślne usunięcie klucza
          delete this.filters[name];
      }
    },

    /**
     * Sprawdza, czy wymagane pola są wypełnione.
     * Ustawia tablicę requireFields i flagę error.
     */
    checkRequireData() {
      this.requireFields = [];
      this.error = false;
      if (!this.filters.dateTimeStart) this.requireFields.push('dateTimeStart');
      if (!this.filters.dateTimeEnd)   this.requireFields.push('dateTimeEnd');
      if (!this.filters.statResolutionUnit) this.requireFields.push('statResolutionUnit');
      if (!this.filters.statResolutionValue) this.requireFields.push('statResolutionValue');
      if (this.requireFields.length) {
        this.error = true;
        this.setErrors(i18n.global.t('required-parameter-missing'));
      }
    },

    checkAllLocations() {
      if (
        !this.filters.locations ||
        this.filters.locations.length !== this.locations.length
      ) {
        this.filters.locations = [];
        let filters = this.filters;
        this.locations.map((item) => {
          filters.locations.push(item.id);
        });
      } else {
        delete this.filters.locations;
      }
    },

    /**
     * Ustawia komunikat o błędzie w authStore
     */
    setErrors(error) {
      console.log(`Setting error: ${error}`);
      this.authStore.errorMessage = error;
    },
  },
});

// // Obsługa Hot Module Replacement (HMR) w trakcie developmentu
// if (typeof import.meta !== 'undefined' && import.meta.hot) {
//   import.meta.hot.accept(
//     acceptHMRUpdate(useCalibrationFiltersStore, import.meta.hot)
//   );
// }